/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Flame, X, MessageCircle, Eye, Bell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return `il y a ${Math.floor(diff / 86400)} j`;
  } catch { return ""; }
}

export default function HotLeadsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    axios
      .get(`${API}/admin/hot-leads`, { withCredentials: true })
      .then((r) => setAlerts(r.data || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // Poll every 60s so a hot lead created in another tab pops up here too.
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, []);

  const acknowledge = async (alertId) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    try {
      await axios.post(`${API}/admin/hot-leads/${alertId}/ack`, {}, { withCredentials: true });
    } catch {
      refresh();
    }
  };

  if (loading || alerts.length === 0) return null;

  return (
    <div
      data-testid="hot-leads-banner"
      className="mb-8 rounded-2xl border-2 border-[#E05A3D] bg-gradient-to-br from-[#FFF5F0] to-[#FFE8DF] overflow-hidden"
    >
      <div className="px-5 py-3 bg-gradient-to-r from-[#E05A3D] to-[#C84B31] text-white flex items-center gap-2">
        <Bell size={16} className="animate-pulse" />
        <span className="font-display font-bold text-sm uppercase tracking-wider">
          {alerts.length} lead{alerts.length > 1 ? "s" : ""} chaud{alerts.length > 1 ? "s" : ""} à recontacter !
        </span>
      </div>
      <div className="divide-y divide-[#E05A3D]/15">
        {alerts.map((a) => (
          <div
            key={a.id}
            data-testid={`hot-lead-card-${a.id}`}
            className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E05A3D] to-[#C84B31] grid place-items-center flex-shrink-0">
                <Flame size={22} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-[#1C3A27] truncate">
                  <span className="text-base">{a.client_name}</span>
                  <span className="ms-2 text-xs font-semibold text-[#E05A3D]">
                    {a.count} actions / 30j
                  </span>
                </p>
                <p className="text-xs text-[#5C6B5C] truncate">
                  {a.client_email} · {fmtRelative(a.created_at)}
                  {a.whatsapp_status === "sent" && !a.whatsapp_mocked && (
                    <span className="ms-2 inline-flex items-center gap-1 text-[#1C7A3C] font-semibold">
                      <MessageCircle size={11} /> WhatsApp envoyé
                    </span>
                  )}
                  {a.whatsapp_mocked && (
                    <span className="ms-2 text-amber-700 font-semibold">(MOCK)</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={`/dashboard/owner/clients?focus=${a.client_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-[#1C3A27] text-sm font-semibold border border-[#1C3A27]/15 hover:border-[#1C3A27]/40"
              >
                <Eye size={14} /> Voir
              </Link>
              <button
                type="button"
                data-testid={`ack-hot-lead-${a.id}`}
                onClick={() => acknowledge(a.id)}
                className="p-2 rounded-lg text-[#5C6B5C] hover:bg-white hover:text-[#1C3A27]"
                title="Marquer comme vu"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
